import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Workspace } from "../lib/api";
import { useConfirm } from "./ConfirmDialog";
import { EnvDot, EnvEditor } from "../pages/Environments";

interface Props {
  workspace: Workspace;
  open: boolean;
  onClose: () => void;
}

// Modal version of the Environments page. Same affordances as the page but
// rendered as an overlay so users can edit envs without leaving their
// current request. Selected env is held in local state — not in the URL —
// because the modal is transient.
export default function EnvironmentsModal({ workspace, open, onClose }: Props) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const list = useQuery({
    queryKey: ["environments", workspace.id],
    queryFn: () => api.environments.list(workspace.id),
    enabled: open,
  });
  const envs = list.data?.environments ?? [];

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // When the modal opens (or env list arrives), pick the default env if
  // none is selected yet.
  useEffect(() => {
    if (!open) return;
    if (selectedId && envs.some((e) => e.id === selectedId)) return;
    const def = envs.find((e) => e.is_default) ?? envs[0];
    setSelectedId(def?.id ?? null);
  }, [open, envs, selectedId]);

  const selected = useMemo(() => envs.find((e) => e.id === selectedId) ?? null, [envs, selectedId]);

  const create = useMutation({
    mutationFn: () =>
      api.environments.create({ workspace_id: workspace.id, name: "Untitled environment", color: null }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["environments", workspace.id] });
      setSelectedId(r.id);
    },
  });

  // Close on Escape, just like other modals.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-[color:var(--backdrop)]" onClick={onClose}>
      <div
        className="card w-full max-w-5xl h-[min(820px,90vh)] overflow-hidden flex flex-col shadow-[var(--shadow-strong)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">Environments</h2>
            <span className="text-xs muted-strong">— {workspace.name}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2"
          >×</button>
        </div>
        <div className="flex-1 min-h-0 flex">
          <aside className="w-56 shrink-0 border-r border-border bg-surface flex flex-col min-h-0">
            <div className="flex items-center justify-between px-3 py-2 border-b border-border">
              <div className="text-[10px] uppercase tracking-wider muted-strong font-semibold">Environments</div>
              <button
                type="button"
                onClick={() => create.mutate()}
                title="New environment"
                aria-label="New environment"
                disabled={create.isPending}
                className="h-6 w-6 grid place-items-center rounded text-text-faint hover:text-text hover:bg-surface-2 disabled:opacity-50"
              >＋</button>
            </div>
            <div className="flex-1 overflow-y-auto scroll-pane py-1">
              {envs.length === 0 && (
                <div className="p-3"><div className="empty-card">No environments yet. Hit <span className="empty-card-strong">＋</span> above to add one.</div></div>
              )}
              <ul>
                {envs.map((e) => (
                  <li key={e.id}>
                    <button
                      type="button"
                      onClick={() => setSelectedId(e.id)}
                      className={
                        "w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left " +
                        (e.id === selectedId ? "bg-accent-soft text-accent-bright" : "text-text-dim hover:bg-surface-2 hover:text-text")
                      }
                    >
                      <EnvDot color={e.color} />
                      <span className="flex-1 truncate">{e.name}</span>
                      {e.is_default && <span className="chip bg-surface-3 muted-strong text-[10px]">default</span>}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
          <main className="flex-1 min-w-0 overflow-auto scroll-pane bg-bg p-5">
            {selected ? (
              <EnvEditor
                key={selected.id}
                env={selected}
                workspaceId={workspace.id}
                onDeleted={() => {
                  qc.invalidateQueries({ queryKey: ["environments", workspace.id] });
                  setSelectedId(null);
                }}
                confirm={confirm}
              />
            ) : (
              <div className="grid h-full place-items-center muted">
                <p className="text-sm">Pick or create an environment to manage its variables.</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
